import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Users, ArrowLeft, Loader2, PhoneOff, MessageSquare, Send, XCircle } from 'lucide-react';
import api, { liveAPI, API_BASE } from '../api/client';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';

const ICE_SERVERS = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
    ]
};

export default function MeetingRoomPlayer({ roomId, secretCode, myUserName, onLeave }) {
    const [participants, setParticipants] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);
    const [myUserId, setMyUserId] = useState(null);

    // Chat State
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const chatEndRef = useRef(null);

    const stompClientRef = useRef(null);
    const localStreamRef = useRef(null);
    const peersRef = useRef(new Map()); // PeerConnection mapped by userId
    const [mediaStreams, setMediaStreams] = useState(new Map()); // WebRTC Remote Media Streams
    const iceQueuesRef = useRef(new Map()); // Ice queue for early transport candidates

    // Local Video Ref
    const localVideoRef = useRef(null);

    useEffect(() => {
        if (localVideoRef.current && localStreamRef.current) {
            localVideoRef.current.srcObject = localStreamRef.current;
        }
    }, [localStreamRef.current]);

    useEffect(() => {
        initRoom();
        return () => cleanup();
    }, [roomId]);

    const initRoom = async () => {
        try {
            // 1. Get Mic & Camera
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: true,
                audio: { echoCancellation: true, noiseSuppression: true } 
            });
            localStreamRef.current = stream;
            
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }

            // The join action was actually performed on the previous page to verify the code.
            // But we need our participant ID. Let's just call joinRoom again, it's idempotent.
            const participantObj = await liveAPI.joinRoom(roomId, myUserName, false, secretCode);
            setMyUserId(participantObj.userId);

            const parts = await liveAPI.getParticipants(roomId);
            setParticipants(parts);

            // 3. Connect STOMP
            connectSTOMP(participantObj.userId);
        } catch (e) {
            alert('Failed to connect media: ' + (e?.response?.data?.message || e.message));
            onLeave();
        }
    };

    const cleanup = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(t => t.stop());
        }
        peersRef.current.forEach(pc => pc.close());
        peersRef.current.clear();

        setMediaStreams(new Map());
        iceQueuesRef.current.clear();

        if (stompClientRef.current) stompClientRef.current.disconnect();
        liveAPI.leaveRoom(roomId).catch(() => { });
    };

    const connectSTOMP = (userId) => {
        const socket = new SockJS(`${API_BASE}/ws/chat`);
        const client = Stomp.over(socket);
        client.debug = () => { };

        client.connect({}, () => {
            setIsConnected(true);

            // Listen for direct P2P Signals
            client.subscribe(`/topic/live/${roomId}/participant/${userId}`, (msg) => {
                const signal = JSON.parse(msg.body);
                handleSignal(signal, userId);
            });

            // Listen for Broadcasts (New Users joined, left, muted)
            client.subscribe(`/topic/live/${roomId}`, (msg) => {
                const signal = JSON.parse(msg.body);
                handleBroadcast(signal, userId);
            });

            // Broadcast that I joined
            client.send(`/app/live/${roomId}/broadcast`, {}, JSON.stringify({
                type: 'PEER_JOINED', senderId: userId, payload: myUserName
            }));

        }, (error) => {
            console.error("STOMP Error", error);
        });

        stompClientRef.current = client;
    };

    const sendSignal = (targetPeerId, type, payload, myId) => {
        if (!stompClientRef.current?.connected) return;
        stompClientRef.current.send(`/app/live/${roomId}/signal/${targetPeerId}`, {}, JSON.stringify({
            type, senderId: myId, payload
        }));
    };

    const handleBroadcast = async (signal, myId) => {
        if (signal.senderId === myId) return;

        if (signal.type === 'PEER_JOINED') {
            const parts = await liveAPI.getParticipants(roomId);
            setParticipants(parts);

            // Only the person who was already in the room creates the offer to the newcomer.
            // Wait, standard mesh: if I was already here (my start time < newcomer start time), I offer.
            // Or simple: everyone offers to PEER_JOINED.
            // To avoid duplicate offers, only one side should offer. Let's say smaller ID offers.
            if (myId < signal.senderId) {
                const pc = createPeerConnection(signal.senderId, myId);
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                sendSignal(signal.senderId, 'OFFER', pc.localDescription, myId);
            }
        }

        if (signal.type === 'PEER_LEFT' || signal.type === 'PEER_KICKED') {
            if (signal.type === 'PEER_KICKED' && signal.senderId === myId) {
                alert("You have been kicked from the meeting.");
                onLeave();
                return;
            }

            const parts = await liveAPI.getParticipants(roomId);
            setParticipants(parts);

            if (peersRef.current.has(signal.senderId)) {
                peersRef.current.get(signal.senderId).close();
                peersRef.current.delete(signal.senderId);
            }

            setMediaStreams(prev => {
                const newMap = new Map(prev);
                newMap.delete(signal.senderId);
                return newMap;
            });
            iceQueuesRef.current.delete(signal.senderId);
        }

        if (signal.type === 'MUTE_STATE_CHANGED') {
            const parts = await liveAPI.getParticipants(roomId);
            setParticipants(parts);
        }

        if (signal.type === 'CHAT_MESSAGE') {
            setMessages(prev => [...prev, {
                id: Date.now() + Math.random(),
                senderId: signal.senderId,
                senderName: signal.senderName || 'User',
                text: signal.payload,
                timestamp: new Date()
            }]);
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }

        if (signal.type === 'CLEAR_CHAT') {
            setMessages([]);
        }
    };

    const handleSignal = async (signal, myId) => {
        if (!peersRef.current.has(signal.senderId)) {
            if (signal.type === 'ICE_CANDIDATE') {
                if (!iceQueuesRef.current.has(signal.senderId)) {
                    iceQueuesRef.current.set(signal.senderId, []);
                }
                iceQueuesRef.current.get(signal.senderId).push(signal.payload);
                return;
            } else if (signal.type !== 'OFFER') {
                return;
            }
        }

        let pc = peersRef.current.get(signal.senderId);

        try {
            switch (signal.type) {
                case 'OFFER':
                    if (!pc) pc = createPeerConnection(signal.senderId, myId);
                    await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));

                    if (iceQueuesRef.current.has(signal.senderId)) {
                        for (const candidate of iceQueuesRef.current.get(signal.senderId)) {
                            await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(e => console.error(e));
                        }
                        iceQueuesRef.current.delete(signal.senderId);
                    }

                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    sendSignal(signal.senderId, 'ANSWER', pc.localDescription, myId);
                    break;
                case 'ANSWER':
                    await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));

                    if (iceQueuesRef.current.has(signal.senderId)) {
                        for (const candidate of iceQueuesRef.current.get(signal.senderId)) {
                            await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(e => console.error(e));
                        }
                        iceQueuesRef.current.delete(signal.senderId);
                    }
                    break;
                case 'ICE_CANDIDATE':
                    if (pc.remoteDescription && pc.remoteDescription.type) {
                        await pc.addIceCandidate(new RTCIceCandidate(signal.payload)).catch(e => console.error(e));
                    } else {
                        if (!iceQueuesRef.current.has(signal.senderId)) {
                            iceQueuesRef.current.set(signal.senderId, []);
                        }
                        iceQueuesRef.current.get(signal.senderId).push(signal.payload);
                    }
                    break;
            }
        } catch (err) {
            console.error("WebRTC Error handling signal:", err);
        }
    };

    const createPeerConnection = (peerId, myId) => {
        const pc = new RTCPeerConnection(ICE_SERVERS);
        peersRef.current.set(peerId, pc);

        pc.onicecandidate = (e) => {
            if (e.candidate) {
                sendSignal(peerId, 'ICE_CANDIDATE', e.candidate, myId);
            }
        };

        pc.ontrack = (event) => {
            const stream = (event.streams && event.streams.length > 0) ? event.streams[0] : new MediaStream([event.track]);
            setMediaStreams(prev => {
                const newMap = new Map(prev);
                newMap.set(peerId, stream);
                return newMap;
            });
        };

        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));
        } else {
            pc.addTransceiver('audio', { direction: 'recvonly' });
            pc.addTransceiver('video', { direction: 'recvonly' });
        }

        return pc;
    };

    const toggleSelfMute = () => {
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMuted(!audioTrack.enabled);

                if (stompClientRef.current?.connected && myUserId) {
                    stompClientRef.current.send(`/app/live/${roomId}/broadcast`, {}, JSON.stringify({
                        type: 'MUTE_STATE_CHANGED', senderId: myUserId, payload: !audioTrack.enabled
                    }));
                }
            }
        }
    };

    const toggleSelfVideo = () => {
        if (localStreamRef.current) {
            const videoTrack = localStreamRef.current.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setIsVideoMuted(!videoTrack.enabled);
            }
        }
    };

    const sendChatMessage = (e) => {
        e.preventDefault();
        if (!chatInput.trim() || !stompClientRef.current?.connected || !myUserId) return;
        stompClientRef.current.send(`/app/live/${roomId}/broadcast`, {}, JSON.stringify({
            type: 'CHAT_MESSAGE', senderId: myUserId, senderName: myUserName, payload: chatInput
        }));
        setChatInput('');
    };

    const getInitials = (name) => name ? name.substring(0, 2).toUpperCase() : '??';

    if (!myUserId) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--lux-bg, #050508)' }}>
                <div className="flex flex-col items-center">
                    <Loader2 size={48} className="text-blue-500 animate-spin mb-4" />
                    <p className="text-white font-medium">Connecting to Meeting...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-6 animate-fade-in relative z-0 flex flex-col" style={{ background: 'var(--lux-bg, #050508)' }}>
            <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden bg-[#050508]">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-500/10 blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[40%] rounded-full bg-purple-500/10 blur-[150px]" />
            </div>

            <div className="w-full max-w-[1400px] mx-auto flex-1 flex flex-col xl:flex-row gap-6">

                {/* Main Video Grid (Left 3 Columns) */}
                <div className="xl:w-3/4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2rem] p-4 md:p-6 relative overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.5)] flex flex-col">
                    
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start relative z-10 mb-6 gap-4">
                        <div>
                            <h1 className="text-2xl font-extrabold text-white mb-2 tracking-tight">Meeting #{roomId}</h1>
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-gray-300 bg-black/40 border border-white/10 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2">
                                    <Users size={14} className="text-blue-400" /> {participants.length}/10 Participants
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 md:gap-3">
                            <button
                                onClick={toggleSelfVideo}
                                className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all duration-300 font-bold text-sm shadow-lg border ${
                                    isVideoMuted 
                                    ? 'bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30' 
                                    : 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30'
                                }`}
                            >
                                {isVideoMuted ? 'Camera Off' : 'Camera On'}
                            </button>
                            <button
                                onClick={toggleSelfMute}
                                className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all duration-300 font-bold text-sm shadow-lg border ${
                                    isMuted 
                                    ? 'bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30' 
                                    : 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30'
                                }`}
                            >
                                {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                                <span className="hidden sm:inline">{isMuted ? 'Muted' : 'Mic Active'}</span>
                            </button>
                            <button
                                onClick={onLeave}
                                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl font-bold transition flex items-center gap-2 shadow-lg"
                            >
                                <PhoneOff size={18} /> Leave
                            </button>
                        </div>
                    </div>

                    {/* Participant Grid */}
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-auto">
                        {/* Self Video */}
                        <div className="flex flex-col items-center relative group w-full aspect-video rounded-xl border border-blue-500/50 bg-black overflow-hidden shadow-lg">
                            <video autoPlay playsInline muted className={`w-full h-full object-cover transform scale-x-[-1] ${isVideoMuted ? 'hidden' : 'block'}`} ref={localVideoRef} />
                            {isVideoMuted && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-3xl font-bold text-gray-500">
                                    {getInitials(myUserName)}
                                </div>
                            )}
                            <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-xs font-bold text-white flex items-center gap-2">
                                You
                                {isMuted && <MicOff size={12} className="text-red-400" />}
                            </div>
                        </div>

                        {/* Remote Videos */}
                        {participants.filter(p => String(p.userId) !== String(myUserId)).map(guest => (
                            <div key={guest.id} className="flex flex-col items-center relative group w-full aspect-video rounded-xl border border-white/10 bg-black overflow-hidden shadow-lg">
                                {mediaStreams.has(guest.userId) ? (
                                    <video autoPlay playsInline className="w-full h-full object-cover transform scale-x-[-1]" ref={(el) => { if(el) el.srcObject = mediaStreams.get(guest.userId); }} />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-3xl font-bold text-gray-500">
                                        {getInitials(guest.userName)}
                                    </div>
                                )}
                                <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-xs font-medium text-white flex items-center gap-2">
                                    {guest.userName}
                                    {guest.muted && <MicOff size={12} className="text-red-400" />}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Chat Panel (Right 1 Column) */}
                <div className="xl:w-1/4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2rem] p-5 md:p-6 flex flex-col shadow-[0_8px_32px_rgba(0,0,0,0.5)] h-[500px] xl:h-[calc(100vh-100px)] relative mt-6 xl:mt-0">
                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/10">
                        <h3 className="text-white font-bold text-lg flex items-center gap-2">
                            <MessageSquare size={20} className="text-blue-400" /> Chat
                        </h3>
                    </div>

                    <div className="flex-1 overflow-y-auto pr-2 space-y-4 mb-4 custom-scrollbar">
                        {messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 opacity-60">
                                <MessageSquare size={40} className="mb-3" />
                                <span className="text-sm">No messages yet.</span>
                            </div>
                        ) : (
                            messages.map((msg) => {
                                const isMe = String(msg.senderId) === String(myUserId);
                                return (
                                    <div key={msg.id} className={`text-[13px] break-words p-3.5 rounded-2xl border shadow-md w-fit max-w-[90%] ${
                                        isMe 
                                        ? 'bg-blue-500/10 border-blue-500/30 ml-auto rounded-tr-sm' 
                                        : 'bg-white/5 border-white/10 rounded-tl-sm'
                                    }`}>
                                        <div className="font-bold mb-1.5 flex justify-between items-center gap-3">
                                            <span className={isMe ? 'text-blue-400' : 'text-gray-300'}>{isMe ? 'You' : msg.senderName}</span>
                                            <span className="text-[10px] text-gray-500 font-normal">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                        <div className="text-gray-200 leading-relaxed text-[13.5px]">{msg.text}</div>
                                    </div>
                                );
                            })
                        )}
                        <div ref={chatEndRef} />
                    </div>

                    <form onSubmit={sendChatMessage} className="flex gap-2 relative mt-auto pt-2">
                        <input
                            type="text"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="Type a message..."
                            className="w-full bg-black/40 border border-white/10 text-white rounded-xl px-5 py-4 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition text-sm pr-14 shadow-inner placeholder-gray-500"
                        />
                        <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 mt-1 bg-blue-600 text-white hover:bg-blue-500 transition-all p-2.5 rounded-lg disabled:opacity-50 shadow-lg" disabled={!chatInput.trim()}>
                            <Send size={18} className="-ml-0.5" />
                        </button>
                    </form>
                </div>
            </div>
            {/* Audio streams for guests without video initialized */}
            {Array.from(mediaStreams.entries()).map(([peerId, stream]) => {
                if (stream.getVideoTracks().length === 0) {
                    return <audio key={peerId} autoPlay playsInline className="hidden" ref={el => { if(el) el.srcObject = stream; }} />;
                }
                return null;
            })}
        </div>
    );
}
